// Base API configuration and utilities
const API_BASE_URL = process.env.REACT_APP_API_URL;

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.cache = new Map();
    this.cacheTimeout = 60000; // 1 minute cache by default
    this.pendingRequests = new Map(); // Track in-flight requests to prevent duplicates
    this.isLoggingOut = false; // Prevent multiple logout attempts
  }

  // Handle 401 Unauthorized - force logout
  handleUnauthorized() {
    // Prevent multiple simultaneous logout attempts
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;

    console.warn('Unauthorized request detected (401). Logging out...');
    
    // Clear all authentication data
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Clear API cache
    this.clearCache();
    
    // Clear all pending requests
    this.pendingRequests.clear();
    
    // Call logout endpoint to clear httpOnly cookies
    fetch(`${this.baseURL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    }).catch(() => {
      // Ignore errors - we're logging out anyway
    }).finally(() => {
      // Redirect to home page and reload to reset app state
      window.location.href = '/';
    });
  }

  // Clear cache for specific endpoint or all cache
  clearCache(endpoint = null) {
    if (endpoint) {
      this.cache.delete(endpoint);
    } else {
      this.cache.clear();
    }
  }

  /** Remove cache entries whose key matches (e.g. all my-bags-page variants). */
  clearCacheIf(keyPredicate) {
    if (typeof keyPredicate !== 'function') return;
    for (const key of this.cache.keys()) {
      if (keyPredicate(key)) {
        this.cache.delete(key);
      }
    }
  }

  // Check if cache entry is still valid
  isCacheValid(cacheEntry, customTimeout = null) {
    const timeout = customTimeout || this.cacheTimeout;
    return Date.now() - cacheEntry.timestamp < timeout;
  }

  // Get cached response if valid
  getCachedResponse(cacheKey, customTimeout = null) {
    const cacheEntry = this.cache.get(cacheKey);
    if (cacheEntry && this.isCacheValid(cacheEntry, customTimeout)) {
      return cacheEntry.data;
    }
    return null;
  }

  // Set cache entry
  setCacheEntry(cacheKey, data, customTimeout = null) {
    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now(),
      timeout: customTimeout || this.cacheTimeout
    });
  }

  // Get default headers (include JWT token from localStorage)
  getHeaders(contentType = 'application/json') {
    const headers = {};
    
    if (contentType) {
      headers['Content-Type'] = contentType;
    }
    
    // Include Authorization header with JWT token from localStorage
    const token = localStorage.getItem('token');
    if (token && token !== 'authenticated_via_cookie') {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const cacheKey = `${options.method || 'GET'}:${endpoint}`;
    const timeoutMs = typeof options.timeoutMs === 'number' ? options.timeoutMs : null;
    
    // Check cache for GET requests unless explicitly disabled
    if ((!options.method || options.method === 'GET') && options.useCache !== false) {
      const cachedResponse = this.getCachedResponse(cacheKey, options.cacheTimeout);
      if (cachedResponse) {
        return cachedResponse;
      }
    }
    
    // Check if same request is already in-flight (request deduplication)
    const requestKey = `${options.method || 'GET'}:${endpoint}:${JSON.stringify(options.body || {})}`;
    if (this.pendingRequests.has(requestKey)) {
      // Return the existing promise instead of making duplicate request
      return this.pendingRequests.get(requestKey);
    }
    
    const controller = timeoutMs ? new AbortController() : null;
    const config = {
      // Only include credentials for authenticated endpoints
      credentials: options.allowUnauthorized ? 'omit' : 'include',
      ...options,
      headers: {
        ...this.getHeaders(options.contentType),
        ...options.headers,
      },
      signal: controller ? controller.signal : undefined,
    };

    // Create the request promise
    const requestPromise = (async () => {
      try {
        let timeoutId;
        if (controller && timeoutMs) {
          timeoutId = setTimeout(() => controller.abort(), timeoutMs);
        }
        const response = await fetch(url, config);
        if (timeoutId) clearTimeout(timeoutId);
        
        // Handle 401 Unauthorized - force logout only if not a public endpoint
        if (response.status === 401 && !options.allowUnauthorized) {
          console.warn(`401 Unauthorized for ${endpoint}. Token in localStorage:`, !!localStorage.getItem('token'));
          this.handleUnauthorized();
          const err = new Error('Session expired. Please log in again.');
          err.status = 401;
          throw err;
        }
        
        // Handle 403 Forbidden - may indicate expired or invalid session
        if (response.status === 403 && !options.allowUnauthorized) {
          const errorData = await response.json().catch(() => ({}));
          // If error message suggests authentication issue, force logout
          const authRelated = errorData.error && (
            errorData.error.toLowerCase().includes('session') ||
            errorData.error.toLowerCase().includes('token') ||
            errorData.error.toLowerCase().includes('unauthorized') ||
            errorData.error.toLowerCase().includes('authentication')
          );
          if (authRelated) {
            this.handleUnauthorized();
          }
        }
        
        // Handle different response types
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const err = new Error(errorData.error || `HTTP error! status: ${response.status}`);
          if (errorData.error_type) {
            err.error_type = errorData.error_type;
          }
          if (errorData.code) {
            err.code = errorData.code;
          }
          if (typeof errorData.can_unarchive === 'boolean') {
            err.canUnarchive = errorData.can_unarchive;
          }
          err.status = response.status;
          throw err;
        }

        // Handle empty responses
        const contentType = response.headers.get('content-type');
        let data;
        if (contentType && contentType.includes('application/json')) {
          data = await response.json();
        } else {
          data = response;
        }
        
        // Cache GET responses unless explicitly disabled
        if ((!options.method || options.method === 'GET') && options.useCache !== false) {
          this.setCacheEntry(cacheKey, data, options.cacheTimeout);
        }
        
        return data;
      } catch (error) {
        console.error(`API request failed: ${endpoint}`, error);
        throw error;
      } finally {
        // Remove from pending requests when done
        this.pendingRequests.delete(requestKey);
      }
    })();
    
    // Store the promise for deduplication
    this.pendingRequests.set(requestKey, requestPromise);
    
    return requestPromise;
  }

  // GET request with caching
  async get(endpoint, options = {}) {
    return this.request(endpoint, {
      method: 'GET',
      ...options,
    });
  }

  // POST request
  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    });
  }

  // PUT request
  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options,
    });
  }

  // PATCH request
  async patch(endpoint, data = {}, options = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
      ...options,
    });
  }

  // DELETE request
  async delete(endpoint, options = {}) {
    return this.request(endpoint, {
      method: 'DELETE',
      ...options,
    });
  }

  // POST with FormData (for file uploads)
  async postFormData(endpoint, formData, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: formData,
      contentType: null, // Don't set content-type for FormData
      ...options,
    });
  }
}

export default new ApiService();
